import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import config from '../config';

// Import Okta auth context type
let getAccessTokenFn: (() => Promise<string | undefined>) | null = null;

// Function to set the token getter (called from App.tsx)
export const setTokenGetter = (tokenGetter: () => Promise<string | undefined>) => {
  getAccessTokenFn = tokenGetter;
};

// Retry configuration
const RETRY_CONFIG = {
  maxRetries: 3,
  baseDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
  retryableStatusCodes: [408, 429, 500, 502, 503, 504],
  retryableErrorCodes: ['ECONNREFUSED', 'ENOTFOUND', 'ECONNRESET', 'ETIMEDOUT', 'ERR_NETWORK']
};

// Exponential backoff delay calculation with Retry-After support
function calculateDelay(attempt: number, error?: AxiosError): number {
  // Check for Retry-After header
  const retryAfter = error?.response?.headers?.['retry-after'];
  if (retryAfter) {
    const delay = parseInt(retryAfter) * 1000; // Convert seconds to milliseconds
    if (!isNaN(delay)) {
      console.log(`⏱️ Respecting Retry-After header: ${delay}ms`);
      return Math.min(delay, RETRY_CONFIG.maxDelay);
    }
  }

  const delay = RETRY_CONFIG.baseDelay * Math.pow(2, attempt - 1);
  return Math.min(delay, RETRY_CONFIG.maxDelay);
}

// Check if error is retryable
function isRetryableError(error: AxiosError): boolean {
  // Network errors
  if (error.code && RETRY_CONFIG.retryableErrorCodes.includes(error.code)) {
    return true;
  }

  // HTTP status codes
  if (error.response?.status && RETRY_CONFIG.retryableStatusCodes.includes(error.response.status)) {
    return true;
  }

  return false;
}

// Retry wrapper for axios requests
async function retryRequest<T>(
  requestFn: () => Promise<T>,
  context: string = 'API request'
): Promise<T> {
  let lastError: AxiosError;

  for (let attempt = 1; attempt <= RETRY_CONFIG.maxRetries; attempt++) {
    try {
      return await requestFn();
    } catch (error) {
      lastError = error as AxiosError;

      // Don't retry if error is not retryable
      if (!isRetryableError(lastError)) {
        throw lastError;
      }

      // Don't retry on last attempt
      if (attempt === RETRY_CONFIG.maxRetries) {
        console.error(`❌ ${context} failed after ${RETRY_CONFIG.maxRetries} attempts:`, lastError.message);
        throw lastError;
      }

      const delay = calculateDelay(attempt, lastError); // Pass error to respect Retry-After
      const statusCode = lastError.response?.status;
      const errorType = (lastError.response?.data as any)?.errorType;

      console.warn(
        `⚠️ ${context} failed (attempt ${attempt}/${RETRY_CONFIG.maxRetries}, status: ${statusCode}, type: ${errorType}), retrying in ${delay}ms...`,
        lastError.message
      );

      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  throw lastError!;
}

// API Response types
export interface ApiResponse<T> {
  status: string;
  message: string;
  data: T;
  timestamp: string;
}

export interface JiraUser {
  accountId: string;
  displayName: string;
  emailAddress: string;
  active: boolean;
}

export interface JiraConnectionResponse {
  status: string;
  message: string;
  user: JiraUser;
  timestamp: string;
}

export interface InitiativeStatus {
  name: string;
  category: string;
  colorName: string;
}

export interface InitiativeIssueType {
  name: string;
  iconUrl: string;
}

export interface InitiativePriority {
  name: string;
  iconUrl: string;
}

export interface InitiativeUser {
  displayName: string;
  emailAddress: string;
  avatarUrls: Record<string, string>;
}

export interface Initiative {
  key: string;
  id: string;
  summary: string;
  status: InitiativeStatus;
  issuetype: InitiativeIssueType;
  priority: InitiativePriority | null;
  assignee: InitiativeUser | null;
  reporter: InitiativeUser | null;
  created: string;
  updated: string;
  description: any;
  labels: string[];
  components: Array<{ name: string }>;
  customFields: {
    orderByField: any;
    startedAtSprint: any;
    endsAtSprint: any;
    sprintField: any;
    theme: any;
    teamSprint: any;
    pmLead: any;
    pmoAssign: any;
  };
  url: string;
  // Story points fields (from team enrichment API)
  totalStoryPoints?: number;
  completedStoryPoints?: number;
  // Team enrichment fields
  teams?: string[];
  team?: string;
  teamSource?: string;
  childEpicTeams?: string[];
  epics?: any[];
}

export interface InitiativesResponse {
  status: string;
  message: string;
  data: Initiative[];
  total: number;
  jql: string;
  timestamp: string;
}

// Release/Version related interfaces
export interface ProjectVersion {
  id: string;
  name: string;
  description?: string;
  released: boolean;
  releaseDate?: string;
  archived: boolean;
  projectId: number;
  isUpcoming?: boolean;
  daysUntilRelease?: number | null;
}

export interface ProjectVersionsResponse {
  status: string;
  message: string;
  data: ProjectVersion[];
  total: number;
  totalUnfiltered?: number;
  timestamp: string;
}

export interface VersionResponse {
  status: string;
  message: string;
  data: ProjectVersion;
  timestamp: string;
}

export interface VersionIssue {
  key: string;
  id: string;
  summary: string;
  status: InitiativeStatus;
  issuetype: InitiativeIssueType;
  priority: InitiativePriority | null;
  assignee: InitiativeUser | null;
  created: string;
  updated: string;
  fixVersions: ProjectVersion[];
  versions: ProjectVersion[];
}

export interface VersionIssuesResponse {
  status: string;
  message: string;
  data: VersionIssue[];
  total: number;
  startAt: number;
  maxResults: number;
  version: ProjectVersion;
  jql: string;
  timestamp: string;
}

export interface ReleaseCacheStats {
  versions: number;
  versionDetails: number;
  versionIssues: number;
  ttl: {
    versions: number;
    versionDetails: number;
    versionIssues: number;
  };
}

export interface ReleaseCacheStatsResponse {
  status: string;
  message: string;
  data: ReleaseCacheStats;
  timestamp: string;
}

// Create axios instance
const apiClient = axios.create({
  baseURL: config.api.baseUrl,
  timeout: 60000, // Increased to 60 seconds for team filter comprehensive enrichment
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
apiClient.interceptors.request.use(
  async (requestConfig) => {
    if (config.features.debugMode) {
      console.log(`🔗 API Request: ${requestConfig.method?.toUpperCase()} ${requestConfig.url}`);
    }

    // Add Okta access token if available and authentication is enabled
    if (config.okta.enabled && getAccessTokenFn) {
      try {
        const accessToken = await getAccessTokenFn();
        if (accessToken) {
          requestConfig.headers = requestConfig.headers || {};
          requestConfig.headers.Authorization = `Bearer ${accessToken}`;

          if (config.features.debugMode) {
            console.log('🔐 Added Okta access token to request');
          }
        }
      } catch (error) {
        console.warn('⚠️ Failed to get access token:', error);
        // Continue with request without token - let the backend handle the 401
      }
    }

    return requestConfig;
  },
  (error) => {
    console.error('❌ API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    if (config.features.debugMode) {
      console.log(`✅ API Response: ${response.status} ${response.config.url}`);
    }

    // Debug timeline data for initiatives endpoint
    if (response.config.url?.includes('/jira/initiatives/red')) {
      const data = response.data?.data;
      if (data && Array.isArray(data) && data.length > 0) {
        console.log(`🔍 API Response Debug - First initiative:`, data[0]);
        console.log(`🔍 Custom fields (full structure):`, JSON.stringify(data[0]?.customFields, null, 2));
        console.log(`🔍 Timeline fields:`, {
          startDate: data[0]?.customFields?.startDate,
          endDate: data[0]?.customFields?.endDate,
          startedAtSprint: data[0]?.customFields?.startedAtSprint,
          endsAtSprint: data[0]?.customFields?.endsAtSprint
        });
        console.log(`🔍 All custom field keys:`, Object.keys(data[0]?.customFields || {}));

        // Look for RED-151656 specifically
        const red151656 = data.find(item => item.key === 'RED-151656');
        if (red151656) {
          console.log(`🎯 Found RED-151656 in BULK API data:`, red151656);
          console.log(`🎯 RED-151656 BULK custom fields:`, JSON.stringify(red151656.customFields, null, 2));
          console.log(`🎯 RED-151656 BULK timeline fields:`, {
            startedAtSprint: red151656.customFields?.startedAtSprint,
            endsAtSprint: red151656.customFields?.endsAtSprint
          });

          // DEBUG: Removed comparison test that was causing 500 errors
          // The bulk API data is logged above for debugging purposes
          /*
          // COMPARISON TEST: Compare bulk API vs single issue API
          console.log(`🔍 COMPARISON TEST: Bulk API vs Single Issue API for RED-151656`);

          // Make a direct call to JIRA API for RED-151656
          fetch('/api/jira/issue/RED-151656')
            .then(response => response.json())
            .then(apiResponse => {
              console.log(`📊 COMPARISON RESULTS:`);
              console.log(`   BULK API startedAtSprint:`, red151656.customFields?.startedAtSprint);
              console.log(`   SINGLE API customfield_10534:`, apiResponse.data?.fields?.customfield_10534);
              console.log(`   BULK API endsAtSprint:`, red151656.customFields?.endsAtSprint);
              console.log(`   SINGLE API customfield_10535:`, apiResponse.data?.fields?.customfield_10535);

              // The issue is clear if these don't match!
              const bulkStart = red151656.customFields?.startedAtSprint;
              const singleStart = apiResponse.data?.fields?.customfield_10534;
              const bulkEnd = red151656.customFields?.endsAtSprint;
              const singleEnd = apiResponse.data?.fields?.customfield_10535;

              if (bulkStart !== singleStart || bulkEnd !== singleEnd) {
                console.log(`🚨 MISMATCH DETECTED!`);
                console.log(`   Bulk API is not returning the same field values as Single API`);
                console.log(`   This explains why timeline data is missing!`);
              } else {
                console.log(`✅ APIs return matching data - issue is elsewhere`);
              }
            })
            .catch(error => {
              console.log(`❌ Direct JIRA API call failed:`, error);
            });
          */
        } else {
          console.log(`❌ RED-151656 not found in API response`);
        }
      }
    }

    return response;
  },
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url;

    console.error(`❌ API Error: ${status} ${url}`, error.response?.data);

    // Handle authentication errors
    if (status === 401) {
      console.warn('🔐 Authentication required - user needs to log in');

      // If Okta is enabled, the ProtectedRoute component will handle redirecting to login
      // For now, just log the error and let the component handle it
      if (config.okta.enabled) {
        console.log('🔄 Okta authentication enabled - ProtectedRoute will handle login redirect');
      }
    } else if (status === 403) {
      console.warn('🚫 Access forbidden - user lacks required permissions');
    }

    return Promise.reject(error);
  }
);

export class ApiService {
  /**
   * Test backend API health with retry logic
   */
  static async testBackendHealth(): Promise<ApiResponse<any>> {
    return retryRequest(
      async () => {
        const response = await apiClient.get('/health');
        return response.data;
      },
      'Backend health check'
    );
  }

  /**
   * Test Jira connection with retry logic
   */
  static async testJiraConnection(): Promise<JiraConnectionResponse> {
    return retryRequest(
      async () => {
        const response = await apiClient.get('/jira/test-connection');
        return response.data;
      },
      'Jira connection test'
    );
  }

  /**
   * Get RED project initiatives with retry logic
   */
  static async getRedInitiatives(): Promise<InitiativesResponse> {
    return retryRequest(
      async () => {
        try {
          const response = await apiClient.get('/jira/initiatives/red');
          return response.data;
        } catch (error: any) {
          // Enhanced error handling for Jira configuration issues
          if (error.response?.status === 400 && error.response?.data?.message?.includes('configuration issue')) {
            throw new Error(`Jira Configuration Issue: ${error.response.data.error?.suggestion || 'Please check your Jira configuration in the Setup page.'}`);
          }
          throw error;
        }
      },
      'RED initiatives fetch'
    );
  }

  /**
   * Search issues with custom JQL
   */
  static async searchIssues(
    jql: string,
    startAt: number = 0,
    maxResults: number = 50
  ): Promise<ApiResponse<any>> {
    const response = await apiClient.get('/jira/search', {
      params: { jql, startAt, maxResults }
    });
    return response.data;
  }

  /**
   * Get specific issue by key
   */
  static async getIssue(issueKey: string): Promise<ApiResponse<any>> {
    const response = await apiClient.get(`/jira/issue/${issueKey}`);
    return response.data;
  }

  /**
   * Get project information
   */
  static async getProject(projectKey: string): Promise<ApiResponse<any>> {
    const response = await apiClient.get(`/jira/project/${projectKey}`);
    return response.data;
  }

  /**
   * Get detailed information about an initiative including epics and stories
   */
  static async getInitiativeDetails(initiativeKey: string): Promise<ApiResponse<any>> {
    const response = await apiClient.get(`/jira/initiative/${initiativeKey}/details`);
    return response.data;
  }

  /**
   * Get child epics for a specific initiative
   */
  static async getInitiativeEpics(
    initiativeKey: string,
    maxResults: number = 50,
    startAt: number = 0,
    includeStoryPoints: boolean = true
  ): Promise<ApiResponse<any>> {
    const response = await apiClient.get(`/jira/initiative/${initiativeKey}/epics`, {
      params: { maxResults, startAt, includeStoryPoints }
    });
    return response.data;
  }

  /**
   * Get comprehensive insights for child epics of a specific initiative
   */
  static async getInitiativeEpicInsights(
    initiativeKey: string,
    maxResults: number = 50,
    startAt: number = 0,
    view: 'all' | 'executive' | 'projectManager' | 'team' = 'all'
  ): Promise<ApiResponse<any>> {
    const response = await apiClient.get(`/jira/initiative/${initiativeKey}/epic-insights`, {
      params: { maxResults, startAt, view }
    });
    return response.data;
  }

  /**
   * Get epic counts per team for in-progress initiatives (for Active by Team calculation)
   */
  static async getEpicCounts(): Promise<ApiResponse<any>> {
    const response = await apiClient.get('/team-filters/epic-counts');
    return response.data;
  }

  /**
   * Get all versions/releases for a project with retry logic
   */
  static async getProjectVersions(
    projectKey: string,
    options: {
      expand?: string;
      orderBy?: string;
      status?: string;
      filterFromDate?: string;
    } = {}
  ): Promise<ProjectVersionsResponse> {
    return retryRequest(
      async () => {
        const params = new URLSearchParams();
        if (options.expand) params.append('expand', options.expand);
        if (options.orderBy) params.append('orderBy', options.orderBy);
        if (options.status) params.append('status', options.status);
        if (options.filterFromDate) params.append('filterFromDate', options.filterFromDate);

        const queryString = params.toString();
        const url = `/jira/project/${projectKey}/versions${queryString ? `?${queryString}` : ''}`;

        const response = await apiClient.get(url);
        return response.data;
      },
      `Project ${projectKey} versions fetch`
    );
  }

  /**
   * Get specific version/release details with retry logic
   */
  static async getVersion(
    versionId: string,
    options: {
      expand?: string;
    } = {}
  ): Promise<VersionResponse> {
    return retryRequest(
      async () => {
        const params = new URLSearchParams();
        if (options.expand) params.append('expand', options.expand);

        const queryString = params.toString();
        const url = `/jira/version/${versionId}${queryString ? `?${queryString}` : ''}`;

        const response = await apiClient.get(url);
        return response.data;
      },
      `Version ${versionId} fetch`
    );
  }

  /**
   * Get issues associated with a version/release with retry logic
   */
  static async getVersionIssues(
    versionId: string,
    options: {
      maxResults?: number;
      startAt?: number;
      fields?: string[];
      issueTypes?: string[];
      status?: string[];
      project?: string;
    } = {}
  ): Promise<VersionIssuesResponse> {
    return retryRequest(
      async () => {
        const params = new URLSearchParams();
        if (options.maxResults) params.append('maxResults', options.maxResults.toString());
        if (options.startAt) params.append('startAt', options.startAt.toString());
        if (options.fields) params.append('fields', options.fields.join(','));
        if (options.issueTypes) params.append('issueTypes', options.issueTypes.join(','));
        if (options.status) params.append('status', options.status.join(','));
        if (options.project) params.append('project', options.project);

        const queryString = params.toString();
        const url = `/jira/version/${versionId}/issues${queryString ? `?${queryString}` : ''}`;

        const response = await apiClient.get(url);
        return response.data;
      },
      `Version ${versionId} issues fetch`
    );
  }

  /**
   * Get RED project releases (>= 2025-09-01) with retry logic
   */
  static async getRedProjectReleases(
    options: {
      expand?: string;
      orderBy?: string;
    } = {}
  ): Promise<ProjectVersionsResponse> {
    return retryRequest(
      async () => {
        const params = new URLSearchParams();
        if (options.expand) params.append('expand', options.expand);
        if (options.orderBy) params.append('orderBy', options.orderBy);

        const queryString = params.toString();
        const url = `/jira/releases/red${queryString ? `?${queryString}` : ''}`;

        const response = await apiClient.get(url);
        return response.data;
      },
      'RED project releases fetch'
    );
  }

  /**
   * Get release cache statistics with retry logic
   */
  static async getReleaseCacheStats(): Promise<ReleaseCacheStatsResponse> {
    return retryRequest(
      async () => {
        const response = await apiClient.get('/jira/releases/cache/stats');
        return response.data;
      },
      'Release cache stats fetch'
    );
  }

  /**
   * Clear release cache with retry logic
   */
  static async clearReleaseCache(): Promise<ApiResponse<any>> {
    return retryRequest(
      async () => {
        const response = await apiClient.delete('/jira/releases/cache/clear');
        return response.data;
      },
      'Release cache clear'
    );
  }
}

export { apiClient };
export default ApiService;
